import { Flex, Icon, Box, Text, cssVar } from '@cad-arc';
import React, { MouseEvent, useState } from 'react';
import { AgencyTypeGlobal } from '../global-attributes';
import { FormattedDurationSince } from './formatted-duration-since';
import { Tooltip } from './tooltip';
import { TileTooltip } from './tile-tooltip';
import { TileEvent } from '../types';
import { observer } from 'mobx-react-lite';
import useDimensions from 'react-cool-dimensions';
import { UnitsDisplay } from '../components/units-display';
import { agencyTypeIconNames } from '../helpers/agency-type';

//TODO: create a common base component for this and unit tile
export const EventTile = observer(function EventTile({
    event,
    isSelected,
    isPrimaryEvent,
    isDisabled,
    isFocused,
    isFocusingLeft,
    isFocusingRight,
    onSetPrimaryEvent,
    onSelectEvent,
    hidePrimarySection,
    tooltipOverride,
}: {
    event: TileEvent;
    isSelected: boolean;
    isPrimaryEvent: boolean;
    isDisabled?: boolean;
    isFocused?: boolean;
    isFocusingLeft?: boolean;
    isFocusingRight?: boolean;
    onSetPrimaryEvent: (item: TileEvent) => void;
    onSelectEvent: (item: TileEvent) => void;
    setPrimaryUnitIdAndSelectIfNecessary?: (unit: TileEvent) => void;
    hidePrimarySection?: boolean;
    tooltipOverride?: string;
}): React.ReactElement {
    const [mouseOver, setMouseOver] = useState<boolean>(false);
    const { width } = useDimensions();
    const getTimeDisplay = (): React.ReactElement => {
        if (event.primaryUnitId && event.timeFirstDispatched) {
            return (
                <FormattedDurationSince
                    fontWeight="bold"
                    date={new Date(event.timeFirstDispatched)}
                    color="inherit"
                />
            );
        } else {
            return (
                <FormattedDurationSince
                    fontWeight="bold"
                    date={new Date(event.timeTransmitted)}
                    color="inherit"
                />
            );
        }
    };

    return (
        <Tooltip
            overlay={
                tooltipOverride ?? (
                    <TileTooltip
                        location={event.eventLocation}
                        cfs={event.cfsText ? event.cfsText : event.cfsNumber}
                        callSigns={event.assignedUnits.map((unit) => unit.callSign)}
                        narrative={event.narrative}
                    />
                )
            }
        >
            {/* Wrapper Box to permit tooltip while pointerEvents are 'none' below */}
            <Box>
                <Flex
                    pointerEvents={isDisabled ? 'none' : undefined}
                    cursor={isDisabled ? undefined : 'pointer'}
                    opacity={isDisabled ? '0.4' : undefined}
                    onMouseEnter={() => setMouseOver(true)}
                    onMouseLeave={() => setMouseOver(false)}
                    onClick={(_: MouseEvent) => {
                        onSelectEvent(event);
                    }}
                    w="full"
                    h="70px"
                    py="1"
                    padding="0"
                    alignItems="center"
                    borderRadius="7"
                    marginBottom="2"
                    border={
                        isSelected
                            ? '1px solid var(--arc-colors-brand-default)'
                            : '1px solid var(--arc-colors-text-tertiary)'
                    }
                    background={
                        isFocused && !isFocusingLeft && !isFocusingRight
                            ? cssVar('arc.colors.brand.emphasisActive')
                            : isSelected
                            ? cssVar('arc.colors.brand.accent')
                            : mouseOver
                            ? cssVar('arc.colors.selected.default')
                            : undefined
                    }
                    color={
                        isFocused && !isFocusingLeft && !isFocusingRight
                            ? cssVar('arc.colors.brand.emphasisContent')
                            : isSelected
                            ? cssVar('arc.colors.brand.accentContent')
                            : mouseOver
                            ? cssVar('arc.colors.selected.content')
                            : undefined
                    }
                >
                    <Box
                        display="flex"
                        borderRadius="7"
                        borderRightRadius="0"
                        pr="1"
                        pt="1"
                        pb="1"
                        minWidth={0}
                        flexGrow={1}
                        h="full"
                        background={
                            isFocused && isFocusingLeft
                                ? cssVar('arc.colors.brand.emphasisActive')
                                : undefined
                        }
                        color={
                            isFocused && isFocusingLeft
                                ? cssVar('arc.colors.brand.emphasisContent')
                                : undefined
                        }
                    >
                        <Flex
                            width="30px"
                            flexDirection="column"
                            justifyContent="space-between"
                            alignItems="center"
                            flexShrink={0}
                            pt="px"
                        >
                            <Icon
                                icon={
                                    agencyTypeIconNames[
                                        event.agencyTypeGlobalAttrId as AgencyTypeGlobal
                                    ]
                                }
                            />
                            <Flex>
                                {isSelected && (
                                    <Icon
                                        size="sm"
                                        icon="Merge"
                                        color={
                                            // show brand color only when left side is not focused
                                            !isFocused || isFocusingRight
                                                ? cssVar('arc.colors.brand.default')
                                                : cssVar('arc.colors.brand.emphasisContent')
                                        }
                                    />
                                )}
                            </Flex>
                        </Flex>
                        <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
                            <Flex alignItems="center" justifyContent="space-between" gap="2">
                                <Flex gap="1">
                                    <Text fontWeight="bold" variant="bodyMd" color="inherit">
                                        {event.eventNumber}
                                    </Text>
                                    <Text variant="bodyMd" noOfLines={1} color="inherit">
                                        {event.cfsNumber.toUpperCase()} - {event?.cfsText}
                                    </Text>
                                </Flex>
                                <UnitsDisplay
                                    width={width}
                                    units={event.assignedUnits}
                                    minNumberToDisplay={3}
                                />
                            </Flex>
                            <Flex justifyContent="space-between" gap="2">
                                <Box>
                                    <Text
                                        noOfLines={1}
                                        fontWeight="bold"
                                        variant="bodyMd"
                                        color="inherit"
                                    >
                                        {event.eventLocation ?? '\u00A0'}
                                    </Text>
                                </Box>
                                <Flex gap="2" whiteSpace="nowrap">
                                    <Flex>
                                        <Text variant="bodyMd" color="inherit">
                                            D-Area:&nbsp;
                                        </Text>
                                        <Text fontWeight="bold" variant="bodyMd" color="inherit">
                                            {event.dispatchArea ? event.dispatchArea : ''}
                                        </Text>
                                    </Flex>
                                    <Flex>
                                        <Text variant="bodyMd" color="inherit">
                                            {event.primaryUnitId ? 'WM' : 'SM'}:&nbsp;
                                        </Text>
                                        <Text variant="bodyMd" noOfLines={1} color="inherit">
                                            {getTimeDisplay()}
                                        </Text>
                                    </Flex>
                                </Flex>
                            </Flex>
                            <Flex>
                                <Text noOfLines={1} variant="caption" color="inherit">
                                    {event.narrative ? event.narrative : ''}
                                </Text>
                            </Flex>
                        </Box>
                    </Box>
                    {!hidePrimarySection && (
                        <Box
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onSetPrimaryEvent(event);
                            }}
                            w="30px"
                            h="full"
                            display="flex"
                            flexShrink={0}
                            justifySelf="flex-end"
                            justifyContent="center"
                            alignItems="center"
                            data-testid={`primary-button-${event.id}`}
                            borderLeft="sm"
                            borderLeftColor={
                                isSelected && !isFocused
                                    ? cssVar('arc.colors.brand.default')
                                    : cssVar('arc.colors.text.tertiary')
                            }
                            borderRightRadius="7"
                            background={
                                isFocused && isFocusingRight
                                    ? cssVar('arc.colors.brand.emphasisActive')
                                    : undefined
                            }
                            color={
                                isFocused && isFocusingRight
                                    ? cssVar('arc.colors.brand.emphasisContent')
                                    : undefined
                            }
                        >
                            {isPrimaryEvent && (
                                <Text
                                    fontWeight="bold"
                                    variant="bodyMd"
                                    data-testid={`primary-indicator-${event.id}`}
                                    color="inherit"
                                >
                                    P
                                </Text>
                            )}
                        </Box>
                    )}
                </Flex>
            </Box>
        </Tooltip>
    );
});
